import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import DropZone from "../../../components/elements/DropZone/DropZone";
import api from "../../../api";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { setSelectedZipCodeSetId, setSelectedZipCodes } from "../../../redux/reducers/campaignToCreate";

type ZipCodeSet = {
  id: string;
  name: string;
  zipCodes: string[];
  updatedAt: string;
};

const PanelRoot = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const CountCell = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

const Radio = styled("div")(({ theme }: { theme: Theme }) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `2px solid ${theme.palette.brand.gray300}`,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&[data-selected='true']": { borderColor: theme.palette.brand.coral },
  "& span": {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: theme.palette.brand.coral,
  },
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "32px 20px",
  textAlign: "center" as const,
  fontSize: 14,
  color: theme.palette.brand.gray500,
  "& a": {
    color: theme.palette.brand.coral,
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
}));

const LoadingState = styled("div")({
  padding: "32px",
  display: "flex",
  justifyContent: "center",
});

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "16px 0",
}));

const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const TagsWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "10px 16px 12px",
});

const ZipTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const ShowMoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.blueAccent,
  background: "none",
  border: `1px solid ${theme.palette.brand.blueAccent}`,
  cursor: "pointer",
  "&:hover": { opacity: 0.8 },
}));

const TabBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: theme.palette.brand.gray100,
  alignSelf: "flex-start",
}));

const TabButton = styled("button")(({ theme }: { theme: Theme }) => ({
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: theme.palette.brand.gray500,
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 16px",
  borderRadius: 8,
  transition: "all 0.15s ease",
  "&[data-active='true']": {
    background: theme.palette.brand.white,
    color: theme.palette.brand.gray900,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
}));

const UploadContent = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const ZipPreview = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "8px 0",
});

const ZipCount = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  fontWeight: 500,
}));

function parseZipText(raw: string): string[] {
  const result: string[] = [];
  for (const row of raw.split("\n")) {
    for (const part of row.split(",")) {
      const z = part.trim();
      if (z) result.push(z);
    }
  }
  return [...new Set(result)];
}

type AZipCodeSetSelectorPanelProps = {
  panelMode?: boolean;
};

const ZipCodeSetSelectorPanel: FC<AZipCodeSetSelectorPanelProps> = ({ panelMode = false }) => {
  const dispatch = useDispatch();
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const selectedZipCodeSetId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedZipCodeSetId,
  );

  const brandgenAdvertiserId = selectedAdvertiser?.brandgenAdvertiserId ?? null;

  const [activeTab, setActiveTab] = useState<"set" | "upload">("set");
  const [sets, setSets] = useState<ZipCodeSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAllIds, setShowAllIds] = useState<Set<string>>(new Set());
  const [zipText, setZipText] = useState("");
  const [uploadError, setUploadError] = useState(false);

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleTabChange = (tab: "set" | "upload") => {
    setActiveTab(tab);
    if (tab === "upload") {
      dispatch(setSelectedZipCodeSetId(null));
      dispatch(setSelectedZipCodes([]));
    } else {
      dispatch(setSelectedZipCodes([]));
      setZipText("");
    }
  };

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadError(false);
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.readAsText(file);
        reader.addEventListener("load", (event) => {
          try {
            const csv = event.target!.result as string;
            const parsed = parseZipText(csv);
            setZipText(parsed.join("\n"));
            dispatch(setSelectedZipCodes(parsed));
          } catch {
            setUploadError(true);
          }
        });
        reader.addEventListener("error", () => setUploadError(true));
      });
    },
    [dispatch],
  );

  const handleZipTextChange = (value: string) => {
    setZipText(value);
    const parsed = parseZipText(value);
    dispatch(setSelectedZipCodes(parsed));
  };

  useEffect(() => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    const fetch = async () => {
      try {
        const { payload } = await api.getZipCodeSets(brandgenAdvertiserId);
        setSets(Array.isArray(payload) ? payload : []);
      } catch {
        setSets([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [brandgenAdvertiserId]);

  const handleSelect = (set: ZipCodeSet) => {
    if (selectedZipCodeSetId === set.id) {
      dispatch(setSelectedZipCodeSetId(null));
      dispatch(setSelectedZipCodes([]));
    } else {
      dispatch(setSelectedZipCodeSetId(set.id));
      dispatch(setSelectedZipCodes(set.zipCodes));
    }
  };

  if (!selectedAdvertiser) {
    return <AdvertiserPrompt>Please select an advertiser to load zip code sets.</AdvertiserPrompt>;
  }

  if (isLoading) {
    return (
      <LoadingState>
        <CircularProgress size={28} />
      </LoadingState>
    );
  }

  const parsedZips = parseZipText(zipText);
  const displayedManualZips = parsedZips.slice(0, 100);
  const remainingManualZips = parsedZips.length - 100;

  return (
    <PanelRoot>
      {panelMode && (
        <ManageLink>
          To create or manage zip code sets, go to{" "}
          <a href="/zip-codes" target="_blank" rel="noopener noreferrer">
            Zip Code Sets ↗
          </a>
          .
        </ManageLink>
      )}

      <TabBar>
        <TabButton type="button" data-active={String(activeTab === "set")} onClick={() => handleTabChange("set")}>
          Pick a set
        </TabButton>
        <TabButton type="button" data-active={String(activeTab === "upload")} onClick={() => handleTabChange("upload")}>
          Upload manually
        </TabButton>
      </TabBar>

      {activeTab === "upload" ? (
        <UploadContent>
          <DropZone
            explanatoryText="Drop a CSV file with zip codes, or click to browse"
            sampleLink=""
            onDrop={handleDrop}
            didErrorOccurInUpload={uploadError}
          />
          <TextField
            multiline
            rows={4}
            variant="outlined"
            placeholder="Or paste zip codes here (comma or newline separated)"
            value={zipText}
            onChange={(e) => handleZipTextChange(e.target.value)}
            fullWidth
          />
          {parsedZips.length > 0 && (
            <>
              <ZipCount>
                {parsedZips.length.toLocaleString()} zip code{parsedZips.length !== 1 ? "s" : ""}
              </ZipCount>
              <ZipPreview>
                {displayedManualZips.map((zip) => (
                  <ZipTag key={zip}>{zip}</ZipTag>
                ))}
                {remainingManualZips > 0 && (
                  <ShowMoreButton type="button" onClick={() => setZipText(parsedZips.join("\n"))}>
                    +{remainingManualZips.toLocaleString()} more
                  </ShowMoreButton>
                )}
              </ZipPreview>
            </>
          )}
        </UploadContent>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>
            No zip code sets found for <strong>{selectedAdvertiser.name}</strong>.{" "}
            <a href="/zip-codes" target="_blank" rel="noopener noreferrer">
              Go to Zip Code Sets →
            </a>
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th style={{ width: 60 }} />
                <Th>Name</Th>
                <Th>Zip Codes</Th>
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const showAll = showAllIds.has(set.id);
                const displayed = showAll ? set.zipCodes : set.zipCodes.slice(0, 100);
                const isSelected = selectedZipCodeSetId === set.id;
                const remaining = set.zipCodes.length - 100;
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow
                      key={set.id}
                      data-selected={String(isSelected)}
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(set.id)) next.delete(set.id);
                          else next.add(set.id);
                          return next;
                        })
                      }
                    >
                      <Td
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(set);
                        }}
                      >
                        <Radio data-selected={String(isSelected)}>{isSelected && <span />}</Radio>
                      </Td>
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {set.zipCodes.length.toLocaleString()}
                        </CountCell>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={4}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <TagsWrapper>
                            {displayed.map((zip) => (
                              <ZipTag key={zip}>{zip}</ZipTag>
                            ))}
                            {!showAll && remaining > 0 && (
                              <ShowMoreButton
                                type="button"
                                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  setShowAllIds((prev) => new Set([...prev, set.id]));
                                }}
                              >
                                +{remaining.toLocaleString()} more
                              </ShowMoreButton>
                            )}
                          </TagsWrapper>
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}
    </PanelRoot>
  );
};

export default ZipCodeSetSelectorPanel;
